import { useRef, type CSSProperties } from 'react';
import { useFillMeasure } from '../../lib/useFillHeight';

/** Below this much room between the content and the window's floor the word would be a smudge, so it
 *  follows the content instead. */
const MIN_FLOOR_ROOM_PX = 72;

/** The wrapper's inline style: the host class reads these custom properties. */
type WordmarkStyle = CSSProperties & {
  '--instance-wordmark-chars': number;
  '--instance-wordmark-drop'?: string;
  '--instance-wordmark-room'?: string;
};

/** The built-in Chetty wordmark as a page's closing line: oversized, near-invisible decoration.
 *
 *  It never becomes the reason a page scrolls. While the content ends inside the window, the word rests
 *  on the window's bottom edge (`floor`): the wrapper takes exactly the room the content leaves above the
 *  page's bottom padding (`useFillMeasure`) — pulling the page end up when that padding alone would reach
 *  past the window — the word reaches down across the padding to the edge, and it shrinks to fit when
 *  the room is shorter than the full-width word. Only when the content itself runs past the window does
 *  the word follow it (`flow`). Pure decoration: hidden from assistive tech, never selectable, never a
 *  hit target. Rendered once per page by the root template (`web/app/template.tsx`), never by a page or
 *  a plugin bundle. */
export function InstanceWordmark({ className = '' }: { className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const measured = useFillMeasure(ref);
  // The caller's top spacing only separates the word from content it follows; on the floor the stylesheet
  // drops it and it is counted back into the room, so the decision holds steady across both layouts.
  const free = measured === undefined ? undefined : measured.space + measured.marginTop;
  // From the end of the content to the window's floor, the page's bottom padding included.
  const room = measured === undefined || free === undefined ? undefined : free + measured.gutter;
  // Until the room is measured the word takes no space at all (`pending`): drawn in flow first, it made
  // a fitting page flash a scrollbar for the frame before it moved to the floor.
  const placement = room === undefined ? 'pending' : room >= MIN_FLOOR_ROOM_PX ? 'floor' : 'flow';
  const style: WordmarkStyle = { '--instance-wordmark-chars': 6 };
  if (placement === 'floor' && free !== undefined && room !== undefined) {
    style.height = Math.max(free, 0);
    style.marginBottom = Math.min(free, 0);
    style['--instance-wordmark-drop'] = `${room - Math.max(free, 0)}px`;
    style['--instance-wordmark-room'] = `${room}px`;
  }
  return (
    <div
      ref={ref}
      aria-hidden
      data-testid="instance-wordmark"
      data-placement={placement}
      className={`instance-wordmark ${className}`.trim()}
      style={style}
    >
      <span>{'Chetty'}</span>
    </div>
  );
}
